import { useState } from "react";
import { format, startOfMonth, endOfMonth } from "date-fns";
import { ptBR } from "date-fns/locale";
import { CalendarIcon, RotateCcw } from "lucide-react";
import { DateRange } from "react-day-picker";

import { Card } from "@/components/ui/card";
import { Calendar } from "@/components/ui/calendar";
import { ScrollArea } from "@/components/ui/scroll-area";
import { useTransactions } from "@/hooks/useTransactions";
import { TransactionCard } from "@/components/TransactionCard";
import { UnifiedTransactionForm } from "@/components/transactions/UnifiedTransactionForm";
import { FinancialSummaryCards } from "@/components/calendar/FinancialSummaryCards";
import { Button } from "@/components/ui/button";

export function CalendarView() {
  const defaultDateRange: DateRange = {
    from: startOfMonth(new Date()),
    to: endOfMonth(new Date())
  };

  const [selectedRange, setSelectedRange] = useState<DateRange>(defaultDateRange);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingTransactionId, setEditingTransactionId] = useState<string | null>(null);
  const [selectedTransactions, setSelectedTransactions] = useState<Set<string>>(new Set());

  const { transactions, deleteTransaction, toggleTransactionPaid } = useTransactions();

  const filteredTransactions = transactions?.filter(transaction => {
    const transactionDate = new Date(transaction.date);
    if (selectedRange.from && selectedRange.to) {
      return transactionDate >= selectedRange.from && transactionDate <= selectedRange.to;
    }
    if (selectedRange.from) {
      return transactionDate >= selectedRange.from;
    }
    return true;
  });

  const handleEditTransaction = (transactionId: string) => {
    setEditingTransactionId(transactionId);
    setIsDialogOpen(true);
  };

  const handleDeleteTransaction = async (transactionId: string) => {
    await deleteTransaction.mutateAsync(transactionId);
  };

  const handleTogglePaid = async (transactionId: string) => {
    await toggleTransactionPaid.mutateAsync(transactionId);
  };

  const handleSelectTransaction = (transactionId: string) => {
    setSelectedTransactions(prev => {
      const newSet = new Set(prev);
      if (newSet.has(transactionId)) {
        newSet.delete(transactionId);
      } else {
        newSet.add(transactionId);
      }
      return newSet;
    });
  };

  const handleCloseDialog = () => {
    setIsDialogOpen(false);
    setEditingTransactionId(null);
  };

  const handleResetRange = () => {
    setSelectedRange(defaultDateRange);
  };

  return (
    <div className="space-y-6">
      <div className="section-delimited">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-bold">Calendário Financeiro</h1>
            <p className="text-muted-foreground">
              {format(new Date(), "EEEE, dd 'de' MMMM 'de' yyyy", { locale: ptBR })}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm">
              Importar
            </Button>
            <Button variant="outline" size="sm">
              Exportar
            </Button>
            <Button variant="outline" size="sm">
              Imprimir
            </Button>
          </div>
        </div>
      </div>

      <div className="section-delimited-darker">
        <div className="grid gap-6 md:grid-cols-[300px_1fr]">
          <div className="flex flex-col gap-4">
            <Card className="card-elevated p-4">
              <Calendar
                mode="range"
                selected={selectedRange}
                onSelect={(range) => {
                  setSelectedRange(range || defaultDateRange);
                }}
                locale={ptBR}
                className="pointer-events-auto"
              />
            </Card>
            <Button
              variant="outline"
              onClick={handleResetRange}
              className="w-full"
            >
              <RotateCcw className="mr-2 h-4 w-4" />
              Redefinir Período
            </Button>
          </div>

          <Card className="card-elevated">
            <div className="p-4 flex items-center gap-2 border-b">
              <CalendarIcon className="h-4 w-4 text-muted-foreground" />
              <span className="text-sm text-muted-foreground">
                {selectedRange?.from ? (
                  selectedRange.to ? (
                    <>
                      {format(selectedRange.from, "dd/MM/yyyy", { locale: ptBR })} -{" "}
                      {format(selectedRange.to, "dd/MM/yyyy", { locale: ptBR })}
                    </>
                  ) : (
                    format(selectedRange.from, "dd/MM/yyyy", { locale: ptBR })
                  )
                ) : (
                  "Selecione um período"
                )}
              </span>
            </div>

            <div className="p-4">
              <FinancialSummaryCards 
                transactions={filteredTransactions} 
                dateRange={selectedRange}
              />
            </div>

            <ScrollArea className="h-[520px]">
              <div className="p-4 space-y-4">
                {filteredTransactions && filteredTransactions.length > 0 ? (
                  filteredTransactions
                    .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())
                    .map((transaction) => (
                      <TransactionCard
                        key={transaction.id}
                        transaction={transaction}
                        onEdit={handleEditTransaction}
                        onDelete={handleDeleteTransaction}
                        onTogglePaid={handleTogglePaid}
                        isSelected={selectedTransactions.has(transaction.id)}
                        onSelect={handleSelectTransaction}
                      />
                    ))
                ) : (
                  <div className="text-center py-8">
                    <p className="text-muted-foreground">Nenhuma transação encontrada para o período selecionado.</p>
                  </div>
                )}
              </div>
            </ScrollArea>
          </Card>
        </div>
      </div>

      <UnifiedTransactionForm
        isOpen={isDialogOpen}
        onClose={handleCloseDialog}
        transaction={editingTransactionId ? transactions?.find(t => t.id === editingTransactionId) : undefined}
        isEditing={!!editingTransactionId}
        onSave={async (data) => {
          // Handle save logic using the actual transactions hook
          const transformedData = {
            type: data.type.toLowerCase() as "income" | "expense",
            amount: data.amount,
            description: data.description,
            category_id: data.categoryId,
            payment_account_id: data.accountId || "none",
            cost_center_id: data.costCenterId,
            date: new Date(data.date),
            is_paid: data.status === "PAID",
            notes: data.notes || "",
            total_installments: data.installmentCount,
            payment_method: data.paymentMethod || "",
            payment_date: data.status === "PAID" ? new Date(data.date) : undefined,
            supplier: data.type === "EXPENSE" ? data.contactId || "" : "",
            client: data.type === "INCOME" ? data.contactId || "" : "",
            attachments: data.attachments.map(att => ({
              file: att.file,
              description: att.description
            }))
          };

          if (editingTransactionId) {
            await editTransaction.mutateAsync({
              id: editingTransactionId,
              formData: transformedData
            });
            toast.success("Transação atualizada com sucesso!");
          } else {
            await createTransaction.mutateAsync(transformedData);
            toast.success("Transação criada com sucesso!");
          }

          handleCloseDialog();
        }}
      />
    </div>
  );
}
